import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardImg, CardBody, Button, Badge, Spinner, InputGroup, Input, InputGroupText } from "reactstrap";
import Handler from "../Helpers/Handler";
import { getCookie } from "../Helpers/Utils";
import { useNavigate } from "react-router-dom";
import SuperMarket from '../Assets/images/Supermaerket33.jpg'

const Products = () => {
  const sessionId = getCookie("sessionToken");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(8);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [filteredList, setFilteredList] = useState([]);
  const [cartItemsMap, setCartItemsMap] = useState({});
  const [cartData, setCartData] = useState({ items: [], total: 0 });
  const [busyProduct, setBusyProduct] = useState(null);

  const loadProducts = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const response = await Handler({ method: "post", url: `/qr/menu.php`, data: { session_id: sessionId } });
      const menu = response?.menu ?? response?.data?.menu ?? [];
      setProducts(menu); 
      setFilteredList(menu);
      setCategories([...new Set(menu.map(m => m.category_name || "Uncategorized"))]);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const getCart = async () => {
    if (!sessionId) return;
    try {
      const response = await Handler({ method: "get", url: `/cart/get.php?session_id=${sessionId}` });
      if (response?.success) {
        const cart = response.cart;
        setCartData(cart);
        const map = {}; cart.items?.forEach(i => map[i.product_id] = Number(i.qty));
        setCartItemsMap(map);
      } else { setCartData({ items: [], total: 0 }); setCartItemsMap({}); }
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadProducts(); getCart(); }, []);

  useEffect(() => {
    let filtered = products;
    if (activeCat !== "") {
      filtered = filtered.filter(p => (p.category_name || "").toLowerCase() === activeCat.toLowerCase());
    }
    if (searchTerm !== "") {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    setFilteredList(filtered);
    setVisibleCount(8); 
  }, [searchTerm, activeCat, products]);

  const handleFilter = (cat) => setActiveCat(cat);

  const optimisticUpdate = async ({ productId, newQty, apiCall }) => {
    const prev = { ...cartItemsMap };
    setBusyProduct(productId);
    try {
      setCartItemsMap((m) => ({ ...m, [productId]: newQty }));
      const res = await apiCall();
      if (!res?.success) { setCartItemsMap(prev); return false; }
      await getCart();
    } catch { setCartItemsMap(prev); }
    setBusyProduct(null);
    return true;
  };

  const updateCart = async (productId, qty) => {
    if (!sessionId) return alert("Session expired");
    if (qty <= 0) return optimisticUpdate({ productId, newQty: 0, apiCall: () => Handler({ method: "post", url: "/cart/remove.php", data: { session_id: sessionId, product_id: productId } }) });
    optimisticUpdate({ productId, newQty: qty, apiCall: () => Handler({ method: "post", url: "/cart/add.php", data: { session_id: sessionId, products: [{ product_id: productId, qty }] } }) });
  };

  const addToCart = (id) => updateCart(id, 1);
  const increase = (id) => updateCart(id, (cartItemsMap[id] || 0) + 1);
  const decrease = (id) => updateCart(id, (cartItemsMap[id] || 0) - 1);

  const visibleItems = filteredList.slice(0, visibleCount);

  return (
    <React.Fragment>
      <style>{`
        .menu-wrapper { background: #f8f9fa; min-height: 100vh; padding-bottom: 120px; }
        
        /* Banner Styling */
        .banner-container { width: 100%; overflow: hidden; position: relative; }
        .market-img { width: 100%; height: 220px; object-fit: cover; object-position: center; border-radius: 0 0 30px 30px; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
        
        /* Sticky Search Styling */
        .search-container { position: sticky; top: 10px; z-index: 100; margin-top: -35px !important; padding: 0 15px; }
        .search-input-group { border-radius: 50px; overflow: hidden; box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important; border: none; }
        .search-input { border: none !important; padding: 12px 15px !important; font-size: 15px; }
        .search-input:focus { box-shadow: none !important; }

        /* Chips Styling */
        .filter-scroll { display: flex; overflow-x: auto; gap: 10px; padding: 20px 15px; scrollbar-width: none; align-items: center; }
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-chip { padding: 8px 20px; border-radius: 50px; background: #fff; border: 1px solid #eee; white-space: nowrap; font-size: 14px; font-weight: 600; color: #555; transition: 0.3s; box-shadow: 0 2px 5px rgba(0,0,0,0.05); }
        .filter-chip.active { background: #007bff; color: #fff; border-color: #007bff; box-shadow: 0 4px 12px rgba(0,123,255,0.35); }

        /* Product Card Styling */
        .product-card { border: none; border-radius: 20px; overflow: hidden; transition: 0.3s; height: 100%; background: #fff; position: relative; }
        .product-image { height: 160px; object-fit: cover; border-bottom: 1px solid #f9f9f9; }
        .product-title { font-size: 15px; font-weight: 700; color: #222; margin-bottom: 4px; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .product-desc { font-size: 11px; color: #888; line-height: 1.3; height: 28px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; margin-bottom: 8px; }
        
        /* Price & Qty Styling */
        .price-text { font-size: 17px; font-weight: 800; color: #1a1a1a; }
        .unit-text { font-size: 11px; color: #999; margin-left: 2px; }
        .cart-qty-box { display: flex; align-items: center; justify-content: space-between; background: #f8f9fa; border-radius: 12px; padding: 4px; border: 1px solid #eee; }
        .qty-btn { width: 32px; height: 32px; border-radius: 10px !important; padding: 0 !important; font-weight: bold; display: flex; align-items: center; justify-content: center; background: #fff !important; border: 1px solid #eee !important; color: #000 !important; }
        .qty-display { font-weight: 800; font-size: 15px; color: #007bff; }

        /* Mobile Optimization */
        @media (max-width: 576px) {
          .market-img { height: 160px; border-radius: 0 0 20px 20px; }
          .product-card { margin-bottom: 10px; }
          .product-image { height: 130px; }
          .product-title { font-size: 14px; }
          .price-text { font-size: 15px; }
          .container { padding-left: 10px; padding-right: 10px; }
        }

        /* Bottom Cart Bar */
        .bottom-cart-bar { position: fixed; bottom: 20px; left: 15px; right: 15px; background: #1c1c1c; color: #fff; padding: 12px 20px; border-radius: 18px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 10px 30px rgba(0,0,0,0.4); z-index: 1000; }
        .cart-button { background: #ffc107; color: #000; border: none; padding: 10px 22px; border-radius: 12px; font-weight: 800; font-size: 14px; transition: 0.2s; }
      `}</style>

      <div className="menu-wrapper">
        <div className="banner-container">
          <img className="market-img" src={SuperMarket} alt="supermarket" />
        </div>
        
        <Container>
          {/* STICKY SEARCH BAR */}
          <div className="search-container">
            <InputGroup className="search-input-group">
              <InputGroupText className="bg-white border-0 ps-3">
                <i className="bx bx-search text-muted fs-5" />
              </InputGroupText>
              <Input 
                placeholder="Search for items..." 
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <InputGroupText className="bg-white border-0 pe-3 cursor-pointer" onClick={() => setSearchTerm("")}>
                  <i className="bx bx-x-circle text-muted fs-5" />
                </InputGroupText>
              )}
            </InputGroup>
          </div>

          {/* HORIZONTAL CATEGORY SCROLL */}
          <div className="filter-scroll">
            <button className={`filter-chip ${activeCat === "" ? "active" : ""}`} onClick={() => handleFilter("")}>All Categories</button>
            {categories.map((c, i) => (
              <button key={i} className={`filter-chip ${activeCat === c ? "active" : ""}`} onClick={() => handleFilter(c)}>{c}</button>
            ))}
          </div>

          {loading && <div className="text-center py-5"><Spinner color="primary" /></div>}

          {/* PRODUCT GRID (2-Column for Mobile) */}
          <Row className="gx-2 gy-3 px-1">
            {visibleItems.length ? visibleItems.map((p) => {
              const qty = cartItemsMap[p.product_id] || 0;
              const disabling = busyProduct === p.product_id;
              return (
                <Col key={p.product_id} xs="12" md="6" lg="4" xl="3">
                  <Card className="product-card shadow-sm">
                    <div className="position-relative">
                      <CardImg top src={p.image || "https://via.placeholder.com/400x300"} alt={p.name} className="product-image" />
                      {qty > 0 && (
                        <Badge color="primary" pill className="position-absolute" style={{ top: '10px', right: '10px', fontSize: '10px', padding: '5px 8px' }}>
                          In Cart: {qty}
                        </Badge>
                      )}
                    </div>
                    <CardBody className="p-2 p-md-3">
                      <h5 className="product-title">{p.name}</h5>
                      <p className="product-desc">{p.description || "Fresh quality guaranteed."}</p>

                      <div className="d-flex align-items-center mb-2">
                        <span className="price-text">₹{p.price}</span>
                        <span className="unit-text">/ {p.unit}</span>
                      </div>

                      {qty === 0 ? (
                        <Button 
                          color="primary" 
                          outline 
                          className="w-100 rounded-pill fw-bold btn-sm py-2" 
                          disabled={disabling} 
                          style={{ fontSize: '12px', borderWidth: '1.5px' }}
                          onClick={() => addToCart(p.product_id)}
                        >
                          {disabling ? <Spinner size="sm" /> : "ADD"}
                        </Button>
                      ) : (
                        <div className="cart-qty-box">
                          <Button className="qty-btn shadow-sm" disabled={disabling} onClick={() => decrease(p.product_id)}>-</Button>
                          <span className="qty-display">{qty}</span>
                          <Button className="qty-btn shadow-sm" disabled={disabling} onClick={() => increase(p.product_id)}>+</Button>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              );
            }) : (
              <Col xs="12" className="text-center text-muted py-5">
                <i className="bx bx-package fs-1 d-block mb-2 opacity-25"></i>
                No items match your search
              </Col>
            )}
          </Row>

          {visibleCount < filteredList.length && (
            <div className="text-center mt-4 mb-5">
              <Button outline color="primary" className="rounded-pill px-5 fw-bold" style={{ fontSize: '13px' }} onClick={() => setVisibleCount((v) => v + 8)}>
                See More Items
              </Button>
            </div>
          )}
        </Container>

        {/* PREMIUM BOTTOM CART BAR */}
        {cartData.items?.length > 0 && (
          <div className="bottom-cart-bar animate__animated animate__fadeInUp">
            <div className="cart-left">
              <h5 className="m-0 fw-bolder text-warning">₹{cartData.total}</h5>
              <div style={{fontSize: '10px', opacity: 0.7, letterSpacing: '0.5px'}}>{cartData.items.length} {cartData.items.length > 1 ? "ITEMS" : "ITEM"} ADDED</div>
            </div>
            <button className="cart-button shadow-lg" onClick={() => navigate("/cart")}>
              VIEW CART <i className="bx bx-right-arrow-alt ms-1"></i>
            </button>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Products;